import React, { useLayoutEffect } from "react";
import { useFrame, useThree } from "@react-three/fiber";
import { observer } from "mobx-react-lite";
import * as THREE from "three";
import { editorStoreContext } from "../../store/editorStore";
import {
  TWO_D_CAMERA_Z,
  apply2DOrthographicFrustum,
  get2DOrthographicHalfHeight,
} from "../../utils/floorPlan2DCamera";

type FloorPlan2DCameraProps = {
  /** Shared Canvas orthographic camera — must be the only 2D camera instance. */
  camera: THREE.OrthographicCamera;
  /** When false, 3D owns the active R3F camera — do not steal it back. */
  active: boolean;
};

const frustumMatchesWorldUnits = (
  camera: THREE.OrthographicCamera,
  aspect: number,
): boolean => {
  const expectedHalfHeight = get2DOrthographicHalfHeight(TWO_D_CAMERA_Z);
  const expectedHalfWidth = expectedHalfHeight * Math.max(aspect, 0.001);
  return (
    Math.abs(camera.top - expectedHalfHeight) < 1e-4 &&
    Math.abs(camera.right - expectedHalfWidth) < 1e-4
  );
};

/**
 * Keeps the shared top-down 2D orthographic camera frustum in sync with the
 * canvas aspect. Uses one shared camera instance (creating a second one broke
 * panning by fighting the Canvas camera).
 */
const FloorPlan2DCamera: React.FC<FloorPlan2DCameraProps> = observer(
  ({ camera, active }) => {
    const editorStore = React.useContext(editorStoreContext);
    const { set, size, camera: activeCamera } = useThree();

    useLayoutEffect(() => {
      const aspect = size.width / Math.max(size.height, 1);
      apply2DOrthographicFrustum(camera, aspect, TWO_D_CAMERA_Z);
      (camera as THREE.OrthographicCamera & { manual?: boolean }).manual = true;

      if (!active) {
        return;
      }

      if (Math.abs(camera.position.z - TWO_D_CAMERA_Z) > 1e-6) {
        camera.position.z = TWO_D_CAMERA_Z;
      }
      camera.up.set(0, 1, 0);
      camera.lookAt(camera.position.x, camera.position.y, 0);
      camera.updateMatrixWorld();

      if (activeCamera !== camera) {
        set({ camera });
      }
      if (editorStore.camera !== camera) {
        editorStore.setCamera(camera);
      }
    }, [active, activeCamera, camera, editorStore, set, size.height, size.width]);

    // R3F can overwrite ortho left/right/top/bottom from pixel size on resize.
    // Re-assert world-unit frustum so pan clamps stay valid.
    useFrame(() => {
      if (!active) return;
      const aspect = size.width / Math.max(size.height, 1);
      if (!frustumMatchesWorldUnits(camera, aspect)) {
        apply2DOrthographicFrustum(camera, aspect, TWO_D_CAMERA_Z);
        (camera as THREE.OrthographicCamera & { manual?: boolean }).manual = true;
      }
    });

    return null;
  },
);

export default FloorPlan2DCamera;
